import { Injectable, DOCUMENT, inject, signal, computed, Signal } from '@angular/core';
import { ClientLoggingService } from '../client-logging.service';

/**
 * What the user asked for. `system` defers to the operating system's own setting; the other two
 * are explicit and override it.
 */
export type ColorSchemePreference = 'light' | 'dark' | 'system';

/** What the application actually renders, once `system` has been resolved against the OS. */
export type ResolvedColorScheme = 'light' | 'dark';

/**
 * The localStorage key the opt-in has always used (Issue #1710). It is unchanged on purpose:
 * a reader who set `app-color-scheme` to `dark` from the console before this issue existed
 * keeps their dark theme across this upgrade rather than silently reverting to light.
 */
export const COLOR_SCHEME_STORAGE_KEY = 'app-color-scheme';

/**
 * The preference applied when nothing is stored.
 *
 * `light`, not `system`, and that is the whole of this issue's third scope item. See
 * `docs/06-user-interface/color-scheme.md` for the reasoning and for the condition under which
 * this should become `system`. In short: the application SHELL is themed as of this issue, but
 * the routed page bodies behind it are not, and `body`'s background already follows
 * `--mat-sys-surface`. Defaulting to `system` would darken the page surface under content that
 * still paints hardcoded near-black ink, for every dark-OS visitor, without anyone choosing it.
 * `system` remains available as an explicit choice in the control, because a reader who picks it
 * has seen what they are picking and can pick again.
 */
export const DEFAULT_COLOR_SCHEME_PREFERENCE: ColorSchemePreference = 'light';

/** The media query that carries the operating system's own light/dark setting. */
export const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * The browser-chrome colour for each scheme, used for `<meta name="theme-color">`.
 *
 * These duplicate the two values the pre-paint script in `index.html` has to hardcode, because
 * that script runs before the stylesheet is guaranteed to have parsed and so cannot read a
 * computed token. `theme-color-literals.spec.ts` compiles `styles.scss` and asserts that both
 * literals still equal the `--mat-sys-surface` the compiled stylesheet actually produces, so the
 * duplication cannot drift unnoticed.
 */
export const THEME_COLOR_BY_SCHEME: Record<ResolvedColorScheme, string> = {
  light: '#ffffff',
  dark: '#424242',
};

/**
 * Owns the application's colour scheme: which one the reader asked for, which one that resolves
 * to, and the two pieces of document state that express it.
 *
 * ## What this service actually changes
 *
 * There is exactly one switch in the stylesheet. `styles.scss` emits the light `--mat-sys-*`
 * tokens at `:root` and the dark ones under `html[data-theme='dark']`, and
 * `colour-scheme-cascade.spec.ts` compiles the file and asserts that the dark declarations really
 * do win wherever both schemes declare the same property. Everything this service does comes down
 * to setting or removing that one attribute, plus keeping the `theme-color` meta tag in step so
 * the browser's own chrome matches the page underneath it.
 *
 * ## Why `system` is resolved here rather than in CSS
 *
 * The obvious way to honour the operating system's setting is a
 * `@media (prefers-color-scheme: dark)` block in the stylesheet. This service deliberately does
 * not use one, and the reason is in `styles.scss`'s own history: the dark scheme is gated by two
 * different selectors at two different specificities, chosen so that the token declarations tie
 * the light block and win on source order while the component rules sit above Material's light
 * rules but below Angular's component styles. Getting that wrong shipped twice — once leaving the
 * dialog surface white under white text at 1.00:1. A media-query gate would be a third entry
 * point into the same declarations with its own specificity to get right, and it would have to be
 * kept in agreement with the attribute gate forever.
 *
 * Resolving the OS preference in JavaScript instead means the stylesheet keeps its single,
 * already-asserted gate, and `data-theme` stays the one fact that decides the scheme. It also
 * gives the `theme-color` meta tag the same single source of truth, which matters because
 * `<meta name="theme-color" media="...">` accepts only media queries — it cannot key off
 * `[data-theme]`, so while dark is an opt-in there is no static markup that expresses it and the
 * value has to be written from script regardless.
 */
@Injectable({ providedIn: 'root' })
export class ColorSchemeService {
  private readonly document = inject(DOCUMENT);

  /**
   * The live OS setting, or `null` where `matchMedia` is unavailable (older test environments and
   * some server-side renders). A `null` list is treated as "the OS prefers light", which is the
   * same fallback the pre-paint script takes.
   */
  private readonly darkQuery: MediaQueryList | null = this.matchPrefersDark();

  private readonly preferenceSignal = signal<ColorSchemePreference>(this.readStoredPreference());
  private readonly systemPrefersDarkSignal = signal<boolean>(this.darkQuery?.matches ?? false);

  /** What the reader chose: `light`, `dark`, or `system`. */
  readonly preference: Signal<ColorSchemePreference> = this.preferenceSignal.asReadonly();

  /** Whether the operating system is currently asking for dark. */
  readonly systemPrefersDark: Signal<boolean> = this.systemPrefersDarkSignal.asReadonly();

  /** The scheme the application is actually rendering right now. */
  readonly resolvedScheme: Signal<ResolvedColorScheme> = computed(() => {
    const preference = this.preferenceSignal();
    if (preference === 'system') {
      return this.systemPrefersDarkSignal() ? 'dark' : 'light';
    }
    return preference;
  });

  constructor() {
    /*
     * The OS can change its setting while the tab is open — at sunset, on most desktops. That only
     * matters while the reader has chosen `system`, but the listener is unconditional so the
     * signal is already correct at the moment they switch to it.
     *
     * `apply()` has to run here too, and its absence was a real defect caught in review on PR
     * #1995. Updating the signal alone left `resolvedScheme()` correct while `data-theme` and the
     * `theme-color` meta stayed on the previous scheme until the reader touched the control — the
     * service reporting one thing and the document rendering another, which is the exact gap the
     * spec for this class says it exists to close.
     */
    this.darkQuery?.addEventListener('change', event => {
      this.systemPrefersDarkSignal.set(event.matches);
      this.apply();
    });

    // Reconcile the document with the stored preference. The pre-paint script in `index.html` has
    // normally done this already; running it again is a no-op in that case and repairs the
    // document when the script did not run at all, which is how the component tests arrive here.
    this.apply();
  }

  /**
   * Record a new preference, persist it, and repaint the document to match.
   *
   * Persistence is best-effort: a browser with site data blocked throws from `localStorage`, and
   * a reader in that state should still get the scheme they just asked for on the page they are
   * looking at, rather than an error.
   */
  setPreference(preference: ColorSchemePreference): void {
    this.preferenceSignal.set(preference);
    this.writeStoredPreference(preference);
    this.apply();
  }

  /**
   * Flip between the two explicit schemes, taking whatever is on screen now as the starting
   * point. A reader on `system` who toggles gets the opposite of what they were looking at, which
   * is what the word "toggle" promises; it deliberately does not cycle back through `system`,
   * because a three-state cycle makes the next press unpredictable.
   */
  toggle(): void {
    this.setPreference(this.resolvedScheme() === 'dark' ? 'light' : 'dark');
  }

  /**
   * Write the resolved scheme onto the document: the `data-theme` attribute the stylesheet gates
   * on, and the `theme-color` meta tag the browser reads for its own chrome.
   *
   * The attribute is REMOVED rather than set to `light` for the light scheme. `styles.scss` has
   * no `[data-theme='light']` selector — light is what `:root` already says — so an attribute
   * with that value would be inert markup that looks load-bearing, and the next reader would have
   * to compile the stylesheet to discover it does nothing.
   */
  private apply(): void {
    const scheme = this.resolvedScheme();
    const root = this.document.documentElement;

    if (scheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }

    this.applyThemeColorMeta(scheme);
  }

  /**
   * Point `<meta name="theme-color">` at the surface the page is actually rendering.
   *
   * The value is read back from the live `--mat-sys-surface` token wherever that is possible, so
   * the browser chrome matches the painted page rather than a literal that someone has to
   * remember to update. `THEME_COLOR_BY_SCHEME` is the fallback for the case where the token has
   * not resolved yet — a test environment with no stylesheet, chiefly — and is asserted against
   * the compiled stylesheet by its own spec.
   */
  private applyThemeColorMeta(scheme: ResolvedColorScheme): void {
    const meta = this.document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      return;
    }
    meta.setAttribute('content', this.surfaceColorFor(scheme));
  }

  /** The page's current surface colour, falling back to this scheme's documented literal. */
  private surfaceColorFor(scheme: ResolvedColorScheme): string {
    const fallback = THEME_COLOR_BY_SCHEME[scheme];
    const body = this.document.body;
    if (!body || typeof this.document.defaultView?.getComputedStyle !== 'function') {
      return fallback;
    }
    const painted = this.document.defaultView.getComputedStyle(body).backgroundColor;
    // jsdom and a not-yet-parsed stylesheet both yield an empty string or a fully transparent
    // colour here; neither is something to hand the browser as its chrome colour.
    if (!painted || painted === 'transparent' || painted.startsWith('rgba(0, 0, 0, 0)')) {
      return fallback;
    }
    return painted;
  }

  /** `matchMedia` guarded, because jsdom does not implement it. */
  private matchPrefersDark(): MediaQueryList | null {
    const view = this.document.defaultView;
    if (!view || typeof view.matchMedia !== 'function') {
      return null;
    }
    try {
      return view.matchMedia(PREFERS_DARK_QUERY);
    } catch {
      return null;
    }
  }

  /**
   * Read the stored preference, tolerating anything that is not one of the three known values.
   *
   * `localStorage` THROWS rather than returning `null` when site data is blocked, which is the
   * same trap the pre-paint script in `index.html` is wrapped for.
   */
  private readStoredPreference(): ColorSchemePreference {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // Site data blocked — fall through to the default.
    }
    return DEFAULT_COLOR_SCHEME_PREFERENCE;
  }

  /** Persist the preference, or carry on without persistence if the browser will not allow it. */
  private writeStoredPreference(preference: ColorSchemePreference): void {
    try {
      this.document.defaultView?.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
    } catch (error) {
      ClientLoggingService.log(
        'WARN',
        '[ColorSchemeService] Could not persist the colour scheme; site data appears to be blocked.',
        error
      );
    }
  }
}
